import './ShopPage.css';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonImg,
  IonLabel,
  IonPage,
  IonRow,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetStoresQuery, useLazyGetStoreLinkQuery } from "../../services/api/ebioro/ebioro";
import { Store } from "../../types/stores";
import { isPlatform } from '@ionic/react';
import { useAppSelector, useAppDispatch } from "../../services/redux/hook";
import { UserInfo, WalletInfoState } from "../../types/types";
import { useHistory } from "react-router-dom";
import { Provider } from 'react-redux';
import { store } from '@/services/redux/store';
import { closeOutline } from 'ionicons/icons';
import { closeModal } from "@/services/redux/slices";
import { Browser } from '@capacitor/browser';

const Page: React.FC = () => {
  const [getStoreLink] = useLazyGetStoreLinkQuery();
  const { t } = useTranslation();
  const pageRef = useRef();
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const [activeStores, setActiveStores] = useState<Store[]>([]);
  const [comingSoonStores, setComingSoonStores] = useState<Store[]>([]);
  const { data, error, isLoading } = useGetStoresQuery({ page: 1, limit: 10 });
  const user: UserInfo = useAppSelector((state) => state.user);
  const selectedTab: string = useAppSelector((state) => state.selectedTab);
  const [presentAlert] = useIonAlert();

  const history = useHistory();
  const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
  // Alternative loading state for better control
  const [isLoadingStore, setIsLoadingStore] = useState(false);

 
  // Listen for SEP-7 URIs while on shops page
  const openedSep7Uri: string = useAppSelector((state) => state.openedSep7Uri);

  useEffect(() => {
    // Handle SEP-7 URIs received while on shops page
    // Only handle if we're currently the active tab
    if (openedSep7Uri && selectedTab === 'shops') {
      close();
      setTimeout(() => {
    }, 300);
    }
  }, [openedSep7Uri, selectedTab]);


  useEffect(() => {
    if (data?.items) {
      const filtered = data.items.filter((store) =>
        store.storeName.toLowerCase().includes(search.toLowerCase())
      );
      setActiveStores(filtered.filter((s) => s.isActive));
      setComingSoonStores(filtered.filter((s) => !s.isActive));
    }
  }, [search, data]);


  const openStore = async (store: Store) => {
    try {
      // Show custom loading
      setIsLoadingStore(true);

      const result = await getStoreLink({ storeId: store.id, userId: user.id }).unwrap();
      if (!result?.url) throw new Error('Missing store URL');

      if (isPlatform('capacitor')) {
        await Browser.open({ 
          url: result.url,
          presentationStyle: 'fullscreen'
        });
        // Dismiss loading once browser is opened
        setIsLoadingStore(false);
        close();

      } else {
        window.open(result.url, '_blank');
        setIsLoadingStore(false);
      }
    } catch (err) {
      setIsLoadingStore(false);
      presentErrorAlert({ data: t('shops.page.access.error') });
    }
  };

  const presentErrorAlert = (error: any) => {
    presentAlert({
      header: 'Error',
      message: `${error.data}`,
      buttons: [{ text: 'Close', role: 'cancel' }],
    });
  };

    const close = () => {
      dispatch(closeModal());
    }
 

  useEffect(() => {
          if (!walletInfo?.account_key) {
              history.replace("/"); // Redirects without keeping history
          }
      }, [walletInfo, history]);

  return (
    <IonPage ref={pageRef}>
      <IonHeader>
          <IonToolbar style={{ marginTop: "0%" }}>
                  <IonButtons slot='start'>
                    <IonButton onClick={()=>close()} color='primary'>
                      <IonIcon
                        slot="icon-only"
                        icon={closeOutline}
                      />
                    </IonButton>
                  </IonButtons>
              
                </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">{t('shops.page.header') as string}</IonTitle>
          </IonToolbar>
        </IonHeader>        {isLoading && <IonSpinner name="dots" />}
        {error && <div style={{ padding: 16, color: 'red' }}>{t('shops.page.loadFailed')}</div> as any}

        <IonGrid className="ion-padding">
          <IonRow>
            {activeStores.map((store, index) => (
              <IonCol size="6" key={store.id || index} style={{ padding: '8px' }}>
                <IonCard
                  button
                  onClick={() => openStore(store)}
                  style={{
                    margin: 0,
                    borderRadius: '16px',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.08)'
                  }}
                  className="store-card"
                >
                  <IonCardContent style={{ padding: '20px', height: '100%' }}>
                    <div
                      className="store-icon-wrapper"
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px',
                        padding: '8px',
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <IonImg
                        className="store-icon"
                        src={store.iconUrl || "/assets/img/default-store.png"}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onIonError={(e) => {
                          const target = e.target as HTMLIonImgElement;
                          target.src = "/assets/img/default-store.png";
                        }}
                      />
                    </div>
                    <IonLabel>
                      <h3 className="ion-text-start" style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>
                        {store.storeName}
                      </h3>
                    </IonLabel>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            ))}
          </IonRow>

          {comingSoonStores.length > 0 && (
            <>
              <IonText style={{ display: 'block', marginTop: '24px', marginBottom: '12px', fontWeight: '500' }}>
                {t('shops.page.comingSoon')}
              </IonText>
              <IonRow>
                {comingSoonStores.map((store, index) => (
                  <IonCol size="6" key={store.id || index} style={{ padding: '8px' }}>
                    <IonCard
                      disabled
                      style={{
                        margin: 0,
                        borderRadius: '16px',
                        height: '100%',
                        opacity: 0.5,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.08)'
                      }}
                      className="store-card inactive"
                    >
                      <IonCardContent style={{ padding: '20px', height: '100%' }}>
                        <div
                          className="store-icon-wrapper"
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '16px',
                            background: 'linear-gradient(135deg, var(--ion-color-medium) 0%, var(--ion-color-medium-shade) 100%)',
                            padding: '8px'
                          }}
                        >
                          <IonImg
                            className="store-icon"
                            src={store.iconUrl || "/assets/img/default-store.png"}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onIonError={(e) => {
                              const target = e.target as HTMLIonImgElement;
                              target.src = "/assets/img/default-store.png";
                            }}
                          />
                        </div>
                        <IonLabel>
                          <h3 className="ion-text-start" style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>
                            {store.storeName}
                          </h3>
                          <IonText color="medium" style={{ fontSize: '12px' }}>
                            Coming soon
                          </IonText>
                        </IonLabel>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                ))}
              </IonRow>
            </>
          )}
        </IonGrid>

        {/* Custom Loading Overlay */}
        {isLoadingStore && (
          <div className="custom-loading-overlay">
            <div className="custom-loading-content">
              <IonSpinner name="crescent" color="primary" />
              <IonText color="primary">
                <p>{t('shops.page.loading.message') || 'Opening store...'}</p>
              </IonText>
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

const ShopPage: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default ShopPage;
